import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { onIdTokenChanged, type User } from "firebase/auth";
import { auth } from "../firebase";
import type { PlatformClaims } from "@acruxsoft/platform-shared";

interface Ctx {
  user: User | null;
  claims: PlatformClaims | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

const C = createContext<Ctx>({ user: null, claims: null, loading: true, refresh: async () => {} });
export const useAuthCtx = () => useContext(C);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [claims, setClaims] = useState<PlatformClaims | null>(null);
  const [loading, setLoading] = useState(true);

  const loadClaims = useCallback(async (u: User | null) => {
    if (!u) { setClaims(null); return; }
    const t = await u.getIdTokenResult(true);
    setClaims(t.claims as unknown as PlatformClaims);
  }, []);

  useEffect(() => {
    return onIdTokenChanged(auth, async (u) => {
      setUser(u);
      try {
        await loadClaims(u);
      } catch (e) {
        console.error("[auth-provider] loadClaims failed", e);
        setClaims(null);
      } finally {
        setLoading(false);
      }
    });
  }, [loadClaims]);

  // Stable refresh identity: it always reads the current user from auth,
  // not a closed-over snapshot. This keeps the context value's reference
  // stable when only refresh would otherwise change between renders, which
  // matters for downstream effects depending on `refresh`.
  const refresh = useCallback(() => loadClaims(auth.currentUser), [loadClaims]);

  const value = useMemo<Ctx>(() => ({ user, claims, loading, refresh }), [user, claims, loading, refresh]);

  return <C.Provider value={value}>{children}</C.Provider>;
}
